/**
 * A refreshed guest can finish ICE with a video m-line whose sender never encodes.
 * The capture that other peers are already sending has to be placed on that sender
 * before the answer, or the guest renegotiates forever with a black picture.
 */
export type AttachedVideoSender = {
  track: MediaStreamTrack | null;
  replaceTrack: (track: MediaStreamTrack) => Promise<void>;
};

export type AttachedVideoTransceiver = {
  direction: string;
  sender: AttachedVideoSender;
  receiver?: { track?: MediaStreamTrack | null };
};

export type AttachResult = 'skipped' | 'already' | 'replaced' | 'added';

/**
 * Attach the live capture to this peer's video sender.
 *
 * Async and failure-propagating: `replaceTrack` returns a promise, and reporting 'replaced'
 * before it settles let a failed replacement answer with an empty video sender while the
 * rejection had no handler.
 */
export async function attachLiveVideoTrack(
  peerConnection: { getTransceivers?: () => AttachedVideoTransceiver[] },
  addTrack: (track: MediaStreamTrack) => void,
  track: MediaStreamTrack | null,
): Promise<AttachResult> {
  if (!track || track.kind !== 'video' || track.readyState !== 'live') return 'skipped';
  const video = (peerConnection.getTransceivers?.() ?? []).find((transceiver) =>
    transceiver.sender.track?.kind === 'video' || transceiver.receiver?.track?.kind === 'video');
  if (!video) {
    addTrack(track);
    return 'added';
  }
  const sending = video.direction === 'sendonly' || video.direction === 'sendrecv';
  if (sending && video.sender.track === track && video.sender.track.readyState === 'live') return 'already';
  if (!sending) video.direction = 'sendonly';
  // Await the replacement so a rejection propagates to the caller before it answers SDP.
  await video.sender.replaceTrack(track);
  return 'replaced';
}

/**
 * One repair for a peer whose ICE is up and whose encoder has not started.
 *
 * `packetsSent` is cumulative, so "greater than zero" is NOT activity: a sender that
 * transmitted and then stopped still reports a large positive number forever. Idleness is a
 * NON-INCREASING counter across two samples within the current capture generation. A retained
 * `encoderImplementation` alone must not clear the samples either.
 */
export class IdleVideoSenderWatch {
  private readonly samples = new Map<string, { packets: number | undefined; count: number }>();

  note(peerId: string, iceUp: boolean, packetsSent: number | undefined, encoderKnown: boolean): boolean {
    if (!iceUp) {
      this.samples.delete(peerId);
      return false;
    }
    const prior = this.samples.get(peerId);
    // A CHANGED counter (up or down) is activity/reset, not a candidate idle sample. This
    // replaces the old "any positive cumulative value is activity" test, which meant a
    // sender that transmitted and then stopped could never be repaired.
    if (!prior || prior.packets !== packetsSent) {
      this.samples.set(peerId, { packets: packetsSent, count: 1 });
      return false;
    }
    // Same (or still-undefined) counter as the previous sample: a candidate idle sample.
    // `encoderKnown` does not reset this — an encoder implementation can be retained after
    // the sender stopped, which is exactly the case the repair exists for.
    prior.count += 1;
    return prior.count === 2;
  }

  clear(peerId: string): void {
    this.samples.delete(peerId);
  }

  clearAll(): void {
    this.samples.clear();
  }
}
